import type { ReviewHistory } from '@/types';

/** The write was accepted by Supabase, or retained only for later offline sync. */
export type PersistenceReceipt = 'persisted' | 'queued';
import { FLAGS } from '@/config/featureFlags';
import { getNativeMirror, mergeQueuedArrays } from '@/lib/native/localStorageMirror';

const QUEUE_KEY = 'vidi-offline-review-queue';
const CARD_UPDATE_QUEUE_KEY = 'vidi-offline-card-update-queue';
const SETTINGS_UPDATE_QUEUE_KEY = 'vidi-offline-settings-update-queue';

interface QueuedReview {
  cardId: string;
  rating: ReviewHistory['rating'];
  reviewDurationMs: number;
  queuedAt: string;
  reviewedAt?: string;
}

export interface QueuedCardUpdate {
  cardId: string;
  updates: Record<string, unknown>;
  queuedAt: string;
}

export interface QueuedSettingsUpdate {
  updates: Record<string, unknown>;
  queuedAt: string;
}

function readQueue<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue<T>(key: string, queue: T[]): void {
  const serialized = JSON.stringify(queue);
  localStorage.setItem(key, serialized);
  // Native only (plan §2.3): these queues hold COMPLETED FSRS ratings that are
  // not in Supabase yet, and WKWebView localStorage is evictable, so losing it
  // silently loses real review work. Mirrored, not moved: the read path stays
  // synchronous (see localStorageMirror's header). Dead code on web.
  if (FLAGS.nativeIOS) getNativeMirror()?.set(key, serialized);
}

/** Mirror a queue removal to Preferences so a restore cannot resurrect a queue
 *  that was already flushed to Supabase. Dead code on web. */
function removeQueue(key: string): void {
  localStorage.removeItem(key);
  if (FLAGS.nativeIOS) getNativeMirror()?.remove(key);
}

// ---------------------------------------------------------------------------
// Pending-count observation (Batch 14).
//
// PURELY ADDITIVE: this layer only observes the queues. It does NOT change what
// gets queued, the order it replays in, or the sync mutex — that logic lives in
// syncOfflineReviews (reviewSlice) and must stay untouched. The offline status
// UI subscribes here to reflect queue size and detect flushes.
//
// Why an explicit in-tab notify instead of the `storage` event alone: the
// `storage` event only fires in OTHER tabs, never the one that wrote. Every
// queue mutation in THIS module happens in the active tab (a review committed
// while offline, or the flush clearing/re-queuing entries), so a storage-only
// observer would never see its own writes. subscribeToPending() therefore
// listens to both: the in-memory listener set (same-tab writes, driven by
// notifyPendingChange) and the `storage` event (cross-tab writes).
// ---------------------------------------------------------------------------

type PendingListener = () => void;
const pendingListeners = new Set<PendingListener>();

/** Fire all in-tab subscribers. Called after every queue mutation below. */
function notifyPendingChange(): void {
  for (const listener of pendingListeners) listener();
}

/**
 * Subscribe to changes in the total pending-queue size. Returns an unsubscribe
 * function. Compatible with React's useSyncExternalStore. Covers same-tab
 * writes (in-memory notify) and cross-tab writes (`storage` event).
 */
export function subscribeToPending(listener: PendingListener): () => void {
  pendingListeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    pendingListeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/**
 * Total mutations waiting to replay after reconnect: queued reviews + card SRS
 * updates + settings deltas. Pure read (no side effects); safe as a
 * useSyncExternalStore snapshot because it returns a primitive number.
 */
export function getPendingCount(): number {
  return (
    getOfflineQueue().length +
    getOfflineCardUpdateQueue().length +
    getOfflineSettingsUpdateQueue().length
  );
}

export function isOfflinePersistenceError(err: unknown): boolean {
  const browserIsOffline = typeof navigator !== 'undefined' && !navigator.onLine;
  return (
    browserIsOffline ||
    err instanceof TypeError ||
    (err instanceof Error && err.message.includes('Failed to fetch'))
  );
}

/** Get all queued offline reviews */
export function getOfflineQueue(): QueuedReview[] {
  return readQueue<QueuedReview>(QUEUE_KEY);
}

/** Add a review to the offline queue */
export function queueOfflineReview(review: Omit<QueuedReview, 'queuedAt'>): void {
  const queue = getOfflineQueue();
  queue.push({ ...review, queuedAt: new Date().toISOString() });
  writeQueue(QUEUE_KEY, queue);
  notifyPendingChange();
}

/** Clear the offline queue after successful sync */
export function clearOfflineQueue(): void {
  removeQueue(QUEUE_KEY);
  notifyPendingChange();
}

/** Check if there are queued reviews waiting to sync */
export function hasOfflineQueue(): boolean {
  return getOfflineQueue().length > 0;
}

/** Queue a card scheduling/image update that must be replayed after reconnect. */
export function queueOfflineCardUpdate(update: Omit<QueuedCardUpdate, 'queuedAt'>): void {
  const queue = getOfflineCardUpdateQueue();
  queue.push({ ...update, queuedAt: new Date().toISOString() });
  writeQueue(CARD_UPDATE_QUEUE_KEY, queue);
  notifyPendingChange();
}

export function getOfflineCardUpdateQueue(): QueuedCardUpdate[] {
  return readQueue<QueuedCardUpdate>(CARD_UPDATE_QUEUE_KEY);
}

export function clearOfflineCardUpdateQueue(): void {
  removeQueue(CARD_UPDATE_QUEUE_KEY);
  notifyPendingChange();
}

/** Queue a user_settings update whose optimistic local value should survive offline use. */
export function queueOfflineSettingsUpdate(update: Omit<QueuedSettingsUpdate, 'queuedAt'>): void {
  const queue = getOfflineSettingsUpdateQueue();
  queue.push({ ...update, queuedAt: new Date().toISOString() });
  writeQueue(SETTINGS_UPDATE_QUEUE_KEY, queue);
  notifyPendingChange();
}

export function getOfflineSettingsUpdateQueue(): QueuedSettingsUpdate[] {
  return readQueue<QueuedSettingsUpdate>(SETTINGS_UPDATE_QUEUE_KEY);
}

export function clearOfflineSettingsUpdateQueue(): void {
  removeQueue(SETTINGS_UPDATE_QUEUE_KEY);
  notifyPendingChange();
}

// ---------------------------------------------------------------------------
// Native cold-launch restore (plan §2.3). Dead code on web.
//
// Runs once at module load, which is the earliest point any of the writers
// above can be reached (they are exports of this module). It is still async,
// so `restore` re-reads localStorage after the await and merges rather than
// overwrites — see mergeQueuedArrays for the exact race and why no rating is
// lost or duplicated.
//
// A flush that runs before the restore lands simply sees an empty queue and
// does nothing; the restored entries replay on the next flush. Nothing is lost
// by being early, so the restore does not need to block startup.
// ---------------------------------------------------------------------------
if (FLAGS.nativeIOS) {
  void getNativeMirror()
    ?.restore([
      { key: QUEUE_KEY, merge: mergeQueuedArrays },
      { key: CARD_UPDATE_QUEUE_KEY, merge: mergeQueuedArrays },
      { key: SETTINGS_UPDATE_QUEUE_KEY, merge: mergeQueuedArrays },
    ])
    .then((written) => {
      // Tell the offline-status UI the pending count changed under it.
      if (written.length > 0) notifyPendingChange();
    });
}
